import React, { useState } from 'react';
import { DrawerHeader } from '@components/drawer-header';
import {
    Alert,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    Stack,
    CircularProgress,
    Box,
    Grid,
    MenuItem,
    Autocomplete,
    Paper,
    useTheme
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { createOperation, getOperationDefaults } from './api';
import { listOperationConfigs } from '../operations-config/api';
import {
  USER_MACHINE_ACCESS_MODES,
  getUserMachineAccessHelpText,
} from '@utils/operation-config-options';

export function OperationsSetupPage() {
    const [name, setName] = useState('');
    const [dbHost, setDbHost] = useState('');
    const [dbUser, setDbUser] = useState('');
    const [dbPassword, setDbPassword] = useState('');

    const [userMachineAccessMode, setUserMachineAccessMode] = useState<string>('selected_machines');
    const [accessedBy, setAccessedBy] = useState<string[]>([]);

    const [loading, setLoading] = useState(false);
    const [fetchingDefaults, setFetchingDefaults] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const configsQuery = useQuery({
        queryKey: ['operation-configs'],
        queryFn: listOperationConfigs,
    });

    const allOperations = (configsQuery.data || []).map((op: any) => op.DATABASE_NAME);

    React.useEffect(() => {
        getOperationDefaults()
            .then((defaults: any) => {
                if (defaults.db_host) setDbHost(defaults.db_host);
                if (defaults.db_user) setDbUser(defaults.db_user);
                if (defaults.db_password) setDbPassword(defaults.db_password);
            })
            .catch((err: any) => {
                console.error("Failed to load defaults", err);
            })
            .finally(() => {
                setFetchingDefaults(false);
            });
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await createOperation(
                name, 
                dbHost, 
                dbUser, 
                dbPassword,
                userMachineAccessMode,
                accessedBy
            );
            setSuccess(`Operação '${name}' criada com sucesso! O banco de dados foi inicializado e já pode ser acessado.`);
            setName(''); // Reset name only, keep config defaults
            setAccessedBy([]);
        } catch (e: any) {
            console.error(e);
            setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Erro desconhecido");
        } finally {
            setLoading(false);
        }
    };

    const theme = useTheme();

    return (
        <DrawerHeader title="Configuração de Operações">
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            bgcolor: 'background.paper',
                            borderLeft: `6px solid ${theme.palette.primary.main}`,
                            borderRadius: 2,
                            mb: 1
                        }}
                    >
                        <Box>
                            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>
                                ADMINISTRAÇÃO DE BANCO DE DADOS
                            </Typography>
                            <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Nova Operação</Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                Instancie e configure um novo banco de dados operacional. Defina as credenciais de acesso, regras de checklist e permissões de visibilidade para usuários do portal.
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
            {fetchingDefaults ? (
                <Stack alignItems="center" mt={4}>
                    <CircularProgress />
                    <Typography variant="body2" mt={2}>Carregando configurações...</Typography>
                </Stack>
            ) : (
                <Stack spacing={2} sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    <Card>
                        <CardContent>
                            <form onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    <Typography variant="h6">Criar Nova Operação</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Preencha os dados abaixo para criar um novo banco de dados operacional.
                                        A criação é imediata e não requer reinicialização do sistema.
                                    </Typography>

                                    <TextField
                                        label="Nome da Operação (Novo Banco)"
                                        required
                                        fullWidth
                                        value={name}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (/^[a-zA-Z0-9_]*$/.test(val)) {
                                                setName(val);
                                            }
                                        }}
                                        placeholder="Ex: Operacao_SP_Sul"
                                        helperText="Este nome identificará o banco de dados no sistema global. Apenas letras, números e _ são permitidos."
                                    />

                                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom>Regras de uso e acesso</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    label="Permissão de condução por usuário"
                                                    value={userMachineAccessMode}
                                                    onChange={(event) => setUserMachineAccessMode(event.target.value)}
                                                    helperText={getUserMachineAccessHelpText(userMachineAccessMode as any)}
                                                >
                                                    {USER_MACHINE_ACCESS_MODES.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    multiple
                                                    options={Array.from(new Set([...allOperations, ...accessedBy]))}
                                                    value={accessedBy}
                                                    onChange={(_, newValue) => setAccessedBy(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Operações com acesso (ACCESSED_BY)"
                                                            helperText="Selecione quais operações podem acessar os dados desta base."
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom>Configuração do Mongo (Herda da última operação)</Typography>
                                        <hr></hr>
                                        <b>A menos que você tenha certeza do que está fazendo não altere nada</b>
                                        <Stack spacing={2}>
                                            <TextField
                                                label="Host"
                                                fullWidth
                                                size="small"
                                                value={dbHost}
                                                onChange={(e) => setDbHost(e.target.value)}
                                            />
                                            <Stack direction="row" spacing={2}>
                                                <TextField
                                                    label="Usuário"
                                                    fullWidth
                                                    size="small"
                                                    value={dbUser}
                                                    onChange={(e) => setDbUser(e.target.value)}
                                                />
                                                <TextField
                                                    label="Senha"
                                                    fullWidth
                                                    size="small"
                                                    type="password"
                                                    value={dbPassword}
                                                    onChange={(e) => setDbPassword(e.target.value)}
                                                />
                                            </Stack>
                                        </Stack>
                                    </Box>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={!name || loading}
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                                    >
                                        {loading ? 'Criando...' : 'Criar Operação'}
                                    </Button>
                                </Stack>
                            </form>
                        </CardContent>
                    </Card>
                </Stack>
            )}
        </DrawerHeader>
    );
}
